import { useState } from 'react'
import { PencilLine, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * How an agent starts a new listing.
 *
 * The chooser is the whole page until a method is picked, and the toggle
 * carries that choice in the header afterwards. Both only ever appear while
 * the listing is still being created — once it exists the agent is on the
 * edit page, which has an ID and no notion of a creation method.
 */
export type ListingCreateMode = 'ai' | 'manual'

const OPTIONS: { mode: ListingCreateMode; title: string; Icon: typeof Sparkles }[] = [
    { mode: 'ai', title: 'Create with AI', Icon: Sparkles },
    { mode: 'manual', title: 'Create manually', Icon: PencilLine },
]

interface ListingCreateModeProps {
    onSelect: (mode: ListingCreateMode) => void
}

export const ListingCreateModeChooser = ({ onSelect }: ListingCreateModeProps) => (
    <div className='flex min-h-[60vh] flex-col items-center justify-center pb-24'>
        <h5 className='font-bold h5 uppercase text-center'>Create new listing</h5>
        <p className='mt-2 text-center text-sm text-muted-foreground'>How would you like to start?</p>

        <div className='mt-8 flex flex-wrap justify-center gap-5'>
            {OPTIONS.map(({ mode, title, Icon }) => (
                <Card
                    key={mode}
                    role='button'
                    tabIndex={0}
                    onClick={() => onSelect(mode)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onSelect(mode)
                        }
                    }}
                    className='group w-44 cursor-pointer items-center justify-center gap-3 py-8 transition-shadow
                    hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/15'>
                    <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 transition-colors group-hover:bg-black'>
                        <Icon className='h-5 w-5 text-black transition-colors group-hover:text-white' />
                    </span>
                    <span className='text-center text-sm font-medium text-black'>{title}</span>
                </Card>
            ))}
        </div>
    </div>
)

interface ListingCreateModeToggleProps {
    mode: ListingCreateMode
    onChange: (mode: ListingCreateMode) => void
}

/**
 * The header offers only the other method: being in AI mode already says
 * so, and a button for it would just be a label. Switching starts the
 * listing over, so it asks first. Nothing is saved at this point, but a
 * half-filled form is still work.
 */
export const ListingCreateModeToggle = ({ mode, onChange }: ListingCreateModeToggleProps) => {
    const [pending, setPending] = useState<ListingCreateMode | null>(null)
    const other = OPTIONS.find((o) => o.mode !== mode)
    if (!other) return null
    const { mode: next, title, Icon } = other

    return (
        <>
            <Button type='button' size='sm' variant='outline' onClick={() => setPending(next)}>
                <Icon />
                {title}
            </Button>
            <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Switch to {title}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Anything entered so far is cleared and the listing starts over.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Stay here</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (pending) onChange(pending)
                                setPending(null)
                            }}>
                            Switch
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}

export default ListingCreateModeChooser
